/**
 * Header notification bell · in-app approval notifications.
 * Badge = unread outstanding items. Clicking an item marks it read and
 * follows its deep link (e.g. #administration?tab=fx&status=pending).
 * Polls every 5 minutes; silent on failure (never blocks the header).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Bell, CheckCircle2, Clock } from "lucide-react";
import { api } from "@/lib/api";

const POLL_MS = 5 * 60 * 1000;

export default function NotificationBell({ className = "", iconClassName = "h-[18px] w-[18px]", testid = "cc-notifications-btn" }) {
  const [data, setData] = useState({ unread: 0, outstanding: 0, notifications: [] });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = useCallback(async () => {
    try { setData((await api.get("/api/notifications")).data); } catch { /* header stays quiet */ }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, POLL_MS); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read_at) {
      try { setData((await api.post(`/api/notifications/${n.id}/read`)).data); } catch { /* ignore */ }
    }
    if (n.link) window.location.hash = n.link.replace(/^#/, "");
  };

  const items = data.notifications.filter((n) => !n.resolved_at).slice(0, 10);
  return (
    <div ref={ref} className={`relative ${className}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-label={`Notifications (${data.unread} unread)`}
        aria-expanded={open} data-testid={testid}
        className="relative flex h-10 w-10 items-center justify-center rounded-full text-slate-600 hover:bg-slate-100">
        <Bell className={iconClassName} />
        {data.unread > 0 && (
          <span data-testid="cc-notifications-badge"
            className="absolute -right-0.5 -top-0.5 min-w-[18px] rounded-full bg-red-600 px-1 text-center text-[10px] font-bold leading-[18px] text-white">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="cc-notifications-menu"
          className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
          <div className="border-b border-slate-100 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
            Notifications · {data.outstanding} outstanding
          </div>
          {items.length === 0 && <p className="px-3 py-4 text-sm text-slate-400" data-testid="cc-notifications-empty">Nothing awaiting your attention.</p>}
          {items.map((n) => (
            <button key={n.id} type="button" onClick={() => openItem(n)} data-testid={`cc-notification-${n.id}`}
              className={`block w-full border-b border-slate-100 px-3 py-2.5 text-left last:border-0 hover:bg-slate-50 ${n.read_at ? "opacity-70" : ""}`}>
              <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                {n.read_at ? <CheckCircle2 className="h-3.5 w-3.5 text-slate-400" /> : <Clock className="h-3.5 w-3.5 text-amber-600" />} {n.title}
              </div>
              <div className="mt-0.5 text-[12px] text-slate-500">{n.body}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
